import { addDays, format, startOfWeek } from 'date-fns';
import { pl } from 'date-fns/locale';
import type { DayOfWeekCode, PlanBoardEmployee, PlanSlotInfo } from './types';

// ---------------------------------------------------------------------------
// Calendar dates
//
// Plan dates are plain calendar strings ("YYYY-MM-DD"). We never pass them to
// `new Date(string)` (that parses as UTC midnight and shifts the day in some
// time zones). Instead we build a local-midnight Date from the parts, and
// convert back with `format(..., 'yyyy-MM-dd')`, which also uses local time.
// ---------------------------------------------------------------------------

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function isDateString(value: string | null | undefined): value is string {
  if (!value) return false;
  const m = DATE_RE.exec(value);
  if (!m) return false;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return toDateString(d) === value;
}

/** Local-midnight Date for a "YYYY-MM-DD" string. */
export function parseDateString(value: string): Date {
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function toDateString(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

export function addDaysToDateString(value: string, days: number): string {
  return toDateString(addDays(parseDateString(value), days));
}

/** Monday of the week containing the given local date. */
export function mondayOf(date: Date): string {
  return toDateString(startOfWeek(date, { weekStartsOn: 1 }));
}

export function mondayOfDateString(value: string): string {
  return mondayOf(parseDateString(value));
}

export function todayDateString(): string {
  return toDateString(new Date());
}

export function isMonday(value: string): boolean {
  return isDateString(value) && parseDateString(value).getDay() === 1;
}

function capitalizeFirst(text: string): string {
  return text.charAt(0).toLocaleUpperCase('pl') + text.slice(1);
}

/** "Pn" */
export function weekdayShort(value: string): string {
  return DAY_CODE_LABELS[dayCodeOf(value)].short;
}

/** "poniedziałek" */
export function weekdayLong(value: string): string {
  return format(parseDateString(value), 'EEEE', { locale: pl });
}

/** "05.01" */
export function dayMonth(value: string): string {
  return format(parseDateString(value), 'dd.MM');
}

/** "Poniedziałek, 5 stycznia 2099" */
export function longDate(value: string): string {
  return capitalizeFirst(format(parseDateString(value), 'EEEE, d MMMM yyyy', { locale: pl }));
}

/** "5–11 stycznia 2099" or "29 grudnia 2098 – 4 stycznia 2099" */
export function weekRangeLabel(weekStart: string): string {
  const start = parseDateString(weekStart);
  const end = addDays(start, 6);
  if (start.getFullYear() !== end.getFullYear()) {
    return `${format(start, 'd MMMM yyyy', { locale: pl })} – ${format(end, 'd MMMM yyyy', { locale: pl })}`;
  }
  if (start.getMonth() !== end.getMonth()) {
    return `${format(start, 'd MMMM', { locale: pl })} – ${format(end, 'd MMMM yyyy', { locale: pl })}`;
  }
  return `${format(start, 'd')}–${format(end, 'd MMMM yyyy', { locale: pl })}`;
}

/** Formats a real timestamp (DateTime from the API) in the user's local time. */
export function formatTimestamp(value: string | null | undefined): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return format(d, 'd.MM.yyyy, HH:mm');
}

const DAY_CODES: DayOfWeekCode[] = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

export function dayCodeOf(value: string): DayOfWeekCode {
  return DAY_CODES[parseDateString(value).getDay()];
}

export const WEEK_DAY_CODES: DayOfWeekCode[] = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

export const DAY_CODE_LABELS: Record<DayOfWeekCode, { short: string; long: string }> = {
  MON: { short: 'Pn', long: 'Poniedziałek' },
  TUE: { short: 'Wt', long: 'Wtorek' },
  WED: { short: 'Śr', long: 'Środa' },
  THU: { short: 'Cz', long: 'Czwartek' },
  FRI: { short: 'Pt', long: 'Piątek' },
  SAT: { short: 'So', long: 'Sobota' },
  SUN: { short: 'Nd', long: 'Niedziela' },
};

// ---------------------------------------------------------------------------
// People and slots
// ---------------------------------------------------------------------------

export function employeeName(e: Pick<PlanBoardEmployee, 'firstName' | 'lastName'>): string {
  return `${e.lastName} ${e.firstName}`;
}

export function slotHours(start?: string | null, end?: string | null): string {
  if (!start || !end) return '';
  return `${start}–${end}`;
}

export function slotTimeKey(start: string, end: string): string {
  return `${start}-${end}`;
}

export function sortSlots(slots: PlanSlotInfo[]): PlanSlotInfo[] {
  return [...slots].sort(
    (a, b) =>
      a.startTime.localeCompare(b.startTime) ||
      a.endTime.localeCompare(b.endTime) ||
      a.shiftName.localeCompare(b.shiftName, 'pl') ||
      a.order - b.order,
  );
}

// ---------------------------------------------------------------------------
// Slot colours
//
// Shift chips are coloured by time of day (by start hour), not by exact hours:
// three quiet tints, so that the status colours (red = error / below minimum,
// amber = warning / below target) and exception marks stand out on the board.
// The exact hours are always printed in the chip, so colour is never the only
// carrier of information. Red, orange, amber, yellow and green families are
// deliberately not used here. Full class strings are listed so Tailwind keeps
// them in the build.
// ---------------------------------------------------------------------------

export type SlotTimeOfDay = 'MORNING' | 'AFTERNOON' | 'NIGHT';

export const TIME_OF_DAY_ORDER: SlotTimeOfDay[] = ['MORNING', 'AFTERNOON', 'NIGHT'];

export const TIME_OF_DAY_LABELS: Record<SlotTimeOfDay, { label: string; hint: string }> = {
  MORNING: { label: 'Ranna', hint: 'zaczyna się między 4:00 a 10:59' },
  AFTERNOON: { label: 'Popołudniowa', hint: 'zaczyna się między 11:00 a 17:59' },
  NIGHT: { label: 'Nocna', hint: 'zaczyna się między 18:00 a 3:59' },
};

const TIME_OF_DAY_CLASSES: Record<SlotTimeOfDay, string> = {
  MORNING:
    'bg-sky-50 text-sky-900 border-sky-200 dark:bg-sky-950/40 dark:text-sky-100 dark:border-sky-900',
  AFTERNOON:
    'bg-violet-50 text-violet-900 border-violet-200 dark:bg-violet-950/40 dark:text-violet-100 dark:border-violet-900',
  NIGHT:
    'bg-slate-200 text-slate-900 border-slate-300 dark:bg-slate-700/70 dark:text-slate-100 dark:border-slate-600',
};

/** Day off: the quietest chip on the board. */
export const DAY_OFF_CLASSES =
  'bg-transparent text-muted-foreground border-border dark:border-slate-700';

function minutesOf(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

export function slotTimeOfDay(start: string): SlotTimeOfDay {
  const minutes = minutesOf(start);
  if (minutes >= 4 * 60 && minutes < 11 * 60) return 'MORNING';
  if (minutes >= 11 * 60 && minutes < 18 * 60) return 'AFTERNOON';
  return 'NIGHT';
}

export function timeOfDayClass(tod: SlotTimeOfDay): string {
  return TIME_OF_DAY_CLASSES[tod];
}

/** Chip classes for a shift starting at `start` ("HH:mm"). */
export function slotColorClass(start: string): string {
  return TIME_OF_DAY_CLASSES[slotTimeOfDay(start)];
}

/** Extracts a readable message from an Apollo/GraphQL error. */
export function errorMessage(error: unknown, fallback: string): string {
  const e = error as any;
  // class-validator errors carry the individual messages in an array.
  const original = e?.graphQLErrors?.[0]?.extensions?.originalError?.message;
  if (Array.isArray(original) && original.length > 0) return original.join('. ');
  const gqlMessage = e?.graphQLErrors?.[0]?.message;
  if (typeof gqlMessage === 'string' && gqlMessage) return gqlMessage;
  if (typeof e?.message === 'string' && e.message) return e.message;
  return fallback;
}
